import React from 'react';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import type { BasePageProps } from '../../../../types/base';
import type { SiteAction } from '@stores/site/action';
import type { UserAction } from '@stores/user/action';
import { BasePage } from '@components/Base/BasePage';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import parse from 'html-react-parser';
import TabNav from '@components/tabNav';
import CountDown from '@components/countDown';
import Avatar from '@components/avatar';
import { formatDateTime } from '@utils/timeUtils';
import { formatNumberToWan } from '@utils/formatNumber';
import { px2rem } from '@utils/px2rem';
import { listenScrollDirection } from '@utils/scrollUtils';
import { getCookie } from '@utils/cookie';
import { copy } from '@utils/copy';
import Toast from '@components/Toast';
import CommonPopup from '@components/commonPopup';
import CommonPopupNew from '@components/commonPopupNew';
import { BASE_IMG_URL, getUserLevelBadge } from '@images/imageUrls';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import styles from './index.module.scss';
import classNames from 'classnames';
import { FontSize } from '@tiptap/extension-text-style';

interface GoodsDetailH5Props extends BasePageProps, RouteComponentProps {
    site?: SiteAction;
    user?: UserAction;
    goodsCode: string;
    desktopMode?: boolean;
    goodsData?: any;
    loading?: boolean;
    error?: any;
    errorCode?: number | string | null;
    goodsList?: any[];
    goodsListTotal?: number;
    goodsListLoading?: boolean;
    getGoodsList?: Function;
    onGoodsSaleStart?: Function;
    defaultAddress?: any;
    getGoodsAddressList?: Function;
    exchangeLoading?: boolean;
    exchangeGoods?: Function;
    lotteryLoading?: boolean;
    lotteryDraw?: Function;
    refreshGoodsData?: Function;
}

interface GoodsDetailH5State {
    loading: boolean;
    error: any;
    pageLoaded: boolean;
    activeIndex: number;
    currentGoodsType: string;
    exchangePopupVisible: boolean;
    insufficientPopupVisible: boolean;
    exchangeSuccessPopupVisible: boolean;
    lotteryAnimationVisible: boolean;
    lotteryEncouragementVisible: boolean;
    lotteryEncouragementMessage: string;
    lotteryEncouragementIcon: string;
    lotteryWinVisible: boolean;
    lotteryPrize: any;
    exchangeImei: string;
    saleTimingVersion: number;
    weChatBrowserGuideVisible: boolean;
    showFixedNav: boolean;
    scrollDirection: 'up' | 'down' | null;
    userLevelInfo: {
        level?: number | string;
        discount?: number | string;
    } | null;
}

// V 币不足时唤起 App 的商城页，仅用于商品详情的余额不足弹窗。
const V_COIN_MALL_APP_URL = 'vivoclub://com.vivo.club/deep?page=coins_collection';
const APP_DOWNLOAD_URL = 'https://m.malink.cn/s/ABbE73';
const APP_OPEN_FALLBACK_DELAY = 2000;
// 开抢前一小时切换为倒计时。
const ONE_HOUR = 60 * 60 * 1000;
// 抽奖动画至少展示一个完整周期，整体时长固定为 2 秒。
const LOTTERY_ANIMATION_DURATION = 2000;
// 商品推荐列表每页请求数量，与列表接口分页参数保持一致。
const GOODS_LIST_PAGE_SIZE = 10;
// 商品下架空态停留 3 秒后自动进入推荐商品或社区首页。
const UNAVAILABLE_REDIRECT_DELAY = 3000;
// 弹幕横跨一个可视区域使用 5 秒，完整周期再按实际轨道距离等比例换算。
const WINNING_BARRAGE_VIEWPORT_DURATION = 5000;
// 未中奖弹框使用的随机鼓励图标。
const LOTTERY_ENCOURAGEMENT_ICONS = ['no1', 'no2', 'no3', 'no4']
    .map((name) => `${BASE_IMG_URL}/goods/${name}.png`);

// 兼容详情和列表接口的抽奖标识字段。
const isLotteryGoods = (goods: any) => Boolean(goods?.isLottery ?? goods?.is_lottery);

// 兑换价格使用 pay_score，goods_score 作为有折扣时展示的原价。
const getGoodsPayScore = (goods: any) => goods?.pay_score ?? goods?.goods_score ?? 0;
const getGoodsOriginalScore = (goods: any) => goods?.goods_score;
const hasGoodsOriginalScore = (goods: any) => {
    const payScore = Number(getGoodsPayScore(goods));
    const originalScore = Number(getGoodsOriginalScore(goods));
    return Number.isFinite(payScore)
        && Number.isFinite(originalScore)
        && originalScore > payScore;
};
const isGoodsDiscountPriceEqual = (goods: any) => {
    const originalScoreValue = getGoodsOriginalScore(goods);
    const payScoreValue = getGoodsPayScore(goods);
    if (originalScoreValue === undefined || originalScoreValue === null
        || payScoreValue === undefined || payScoreValue === null) {
        return false;
    }

    const originalScore = Number(originalScoreValue);
    const payScore = Number(payScoreValue);
    return Number.isFinite(originalScore)
        && Number.isFinite(payScore)
        && originalScore === payScore;
};

const getGoodsSaleActionText = (goods: any) => (
    isLotteryGoods(goods) ? '开始抽奖' : '开始兑换'
);

const getGoodsEndActionText = (goods: any) => (
    isLotteryGoods(goods) ? '结束抽奖' : '结束兑换'
);

const getLotteryParticipantCount = (goods: any) => (
    goods?.lottery?.drawCount
    ?? goods?.lottery?.draw_count
    ?? goods?.participantCount
    ?? goods?.participant_count
    ?? goods?.sellOutCount
    ?? goods?.sell_out_count
    ?? 0
);

// 参与人次兼容接口的原始数字和已缩写的 "147w" / "147万"。
const formatLotteryParticipantCount = (value: any) => {
    if (typeof value === 'string') {
        const abbreviated = value.trim().match(/^([+-]?\d+(?:\.\d+)?)\s*(?:w|万)$/i);
        if (abbreviated) return `${Number(abbreviated[1])}w`;
    }

    return formatNumberToWan(value, { unit: 'w' });
};

const parseGoodsDateTime = (value: any) => {
    if (!value) return 0;
    const timestamp = new Date(String(value).replace(/-/g, '/')).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
};

// 抽奖结束时间以详情接口 lottery.endAt 为准，不再使用商品上下架时间推断。
const getLotteryEndAt = (goods: any) => {
    if (!isLotteryGoods(goods)) return 0;

    return parseGoodsDateTime(
        goods?.lottery?.endAt
        ?? goods?.lottery?.end_at
        ?? goods?.lottery?.endTime
        ?? goods?.lottery?.end_time
    );
};

// 将业务时间拆分为月日时分，开抢和结束提示共用同一套数字块样式。
const getMonthDayTimeParts = (timestamp: number) => {
    const formatted = formatDateTime(timestamp);
    const match = formatted.match(/(?:\d{4}-)?(\d{2})-(\d{2})\s(\d{2}):(\d{2})/);
    return match ? {
        month: match[1],
        day: match[2],
        hour: match[3],
        minute: match[4],
    } : null;
};

// 兑换商品兼容详情接口可能返回的结束时间字段；为空时表示长期有效。
const getExchangeEndAt = (goods: any) => parseGoodsDateTime(
    goods?.delist_at
    ?? goods?.delistAt
    ?? goods?.endAt
    ?? goods?.end_at
    ?? goods?.endTime
    ?? goods?.end_time
    ?? goods?.lastAt
    ?? goods?.last_at
);

// 抽奖库存耗尽时立即结束；库存充足时再根据 lottery.endAt 判断是否到期。
const isLotteryActivityEnded = (goods: any) => {
    if (!isLotteryGoods(goods)) return false;
    if (Number(goods?.goods_stock) <= 0) return true;
    if (!goods?.lottery) return false;

    const lotteryEndAt = getLotteryEndAt(goods);
    if (lotteryEndAt <= 0) return false;

    return lotteryEndAt <= Date.now();
};

const isExchangeActivityEnded = (goods: any) => {
    if (isLotteryGoods(goods)) return false;

    const exchangeEndAt = getExchangeEndAt(goods);
    if (exchangeEndAt <= 0) return false;

    return exchangeEndAt <= Date.now();
};

// 兼容接口常见字段，判断本次抽奖是否未中奖。
const isLotteryNotWon = (res: any) => {
    const responseData = res?.data ?? res?.Data ?? {};
    const data = responseData?.data ?? responseData;
    const lotteryStatus = data?.lottery_status ?? data?.lotteryStatus ?? res?.lottery_status ?? res?.lotteryStatus;
    if (Number(lotteryStatus) === 2) return true;

    const isWin = data?.isWin ?? data?.is_win ?? data?.winning ?? res?.isWin ?? res?.is_win;
    if (isWin === false || isWin === 0 || isWin === '0' || isWin === 'false') return true;

    const prizeType = data?.prizeType ?? data?.prize_type ?? data?.prize?.prizeType ?? data?.prize?.prize_type;
    if (Number(prizeType) === 4) return true;

    return /未中奖|未抽中|谢谢参与|再接再厉/.test(String(res?.msg ?? res?.message ?? ''));
};

// 后端返回 -14006 表示商品已被抢光/售罄，需要前端刷新详情同步本地库存与按钮状态。
const isOutOfStockResponse = (res: any) => Number(res?.code) === -14006;

// 从抽奖接口响应中读取未中奖文案，优先使用 data.message。
const getLotteryResultMessage = (res: any) => {
    const responseData = res?.data ?? res?.Data ?? {};
    const data = responseData?.data ?? responseData;
    return String(
        data?.message
        ?? data?.msg
        ?? responseData?.message
        ?? responseData?.msg
        ?? res?.message
        ?? res?.Message
        ?? res?.msg
        ?? '别灰心，大奖就在转角等你~'
    );
};

const getRandomLotteryEncouragementIcon = () => (
    LOTTERY_ENCOURAGEMENT_ICONS[Math.floor(Math.random() * LOTTERY_ENCOURAGEMENT_ICONS.length)]
);

// 从不同返回结构中提取中奖奖品信息。
const getLotteryPrizeResult = (res: any, goods: any) => {
    const responseData = res?.data ?? res?.Data ?? {};
    const data = responseData?.data ?? responseData;
    const winning = data?.userWinning ?? data?.user_winning ?? data?.winning ?? data;
    const prize = winning?.prize ?? data?.prize ?? winning;
    const goodsImage = typeof goods?.goods_images?.[0] === 'string'
        ? goods.goods_images[0]
        : goods?.goods_images?.[0]?.imgurl;

    return {
        name: prize?.prizeName
            ?? prize?.prize_name
            ?? prize?.name
            ?? data?.prizeName
            ?? data?.prize_name
            ?? data?.goodsName
            ?? data?.goods_name
            ?? goods?.goods_name
            ?? '神秘奖品',
        image: prize?.prizeCover
            ?? prize?.prize_cover
            ?? prize?.prizeIcon
            ?? prize?.prize_icon
            ?? prize?.icon
            ?? prize?.image
            ?? prize?.imgurl
            ?? data?.goodsImage
            ?? data?.goods_image
            ?? data?.goodsImg
            ?? data?.goods_img
            ?? goodsImage
            ?? '',
        quantity: Number(
            winning?.quantity
            ?? winning?.count
            ?? winning?.num
            ?? data?.quantity
            ?? 1
        ) || 1,
        prizeType: Number(prize?.prizeType ?? prize?.prize_type ?? data?.prizeType ?? data?.prize_type),
        // 中奖奖品有效期从 prize.validDays 透传到中奖弹窗，兼容接口下划线命名。
        validDays: prize?.validDays
            ?? prize?.valid_days
            ?? winning?.validDays
            ?? winning?.valid_days
            ?? data?.validDays
            ?? data?.valid_days,
    };
};

const GOODS_TYPE = {
    PHYSICAL: 1,
    VIRTUAL: 2,
    RIGHTS: 3,
    GUESS: 4,
} as const;

const GOODS_TABS = [
    { name: '商品推荐', type: 'recommend', goodsType: undefined },
    { name: '实物', type: 'physical', goodsType: GOODS_TYPE.PHYSICAL },
    { name: '虚拟', type: 'virtual', goodsType: GOODS_TYPE.VIRTUAL },
    { name: 'V粉专属', type: 'fans', goodsType: GOODS_TYPE.RIGHTS },
];

@inject('site', 'user')
@observer
class GoodsDetailH5 extends BasePage<GoodsDetailH5Props, GoodsDetailH5State> {
    state: GoodsDetailH5State = {
        loading: false,
        error: null,
        pageLoaded: true,
        activeIndex: 0,
        currentGoodsType: 'recommend',
        exchangePopupVisible: Boolean((this.props.location.state as any)?.openExchange),
        insufficientPopupVisible: false,
        exchangeSuccessPopupVisible: false,
        lotteryAnimationVisible: false,
        lotteryEncouragementVisible: false,
        lotteryEncouragementMessage: '',
        lotteryEncouragementIcon: LOTTERY_ENCOURAGEMENT_ICONS[0],
        lotteryWinVisible: false,
        lotteryPrize: null,
        exchangeImei: '',
        saleTimingVersion: 0,
        weChatBrowserGuideVisible: false,
        showFixedNav: false,
        scrollDirection: null,
        userLevelInfo: null,
    };

    private goodsTabsRef: HTMLDivElement | null = null;
    private winningBarrageRef: HTMLDivElement | null = null;
    private winningBarrageTrackRef: HTMLDivElement | null = null;
    private scrollCleanup: Function | null = null;
    private goodsListPage = 1;
    private goodsListParams: Record<string, any> = {};
    private goodsListLoadingMore = false;
    // 定时刷新开抢、倒计时和活动结束状态。
    saleBoundaryTimer: number | null = null;
    // 控制抽奖动画的最短展示时长。
    lotteryAnimationTimer: number | null = null;
    lotteryAnimationStartedAt = 0;
    // App 未安装时跳应用商店；页面已离开或失焦时取消回退。
    appOpenFallbackTimer: number | null = null;
    // 商品下架空态的自动跳转定时器。
    unavailableRedirectTimer: number | null = null;
    // 防止同一登录用户重复请求等级权益，并使异步响应在卸载后失效。
    userLevelRequestStarted = false;
    userLevelRequestSequence = 0;

    // 仅依据已有用户状态判断登录，不触发登录跳转。
    isUserLoggedIn = () => Boolean(
        this.props.user?.isLoggedIn
        || this.props.user?.userInfo
        || this.props.user?.token
        || getCookie('user_code')
    );

    // 从 user.level 响应中提取用户等级和对应的商城折扣。
    parseUserLevelInfo = (res: any) => {
        const data = res?.data?.data || res?.data || {};
        const level = data?.level?.level ?? data?.level;
        const normalizedLevel = Number(level);
        const benefits = Array.isArray(data?.benefits) ? data.benefits : [];
        const discountKey = normalizedLevel === 7 ? 'mall_discount_v7' : 'mall_discount';
        const discount = benefits.find((item: any) => item?.key === discountKey)?.value;

        // 商城折扣只对 V6、V7 展示，其他等级不生成折扣标签。
        if (![6, 7].includes(normalizedLevel) || !getUserLevelBadge(normalizedLevel)) {
            return null;
        }
        if (discount === undefined || discount === null || !Number.isFinite(Number(discount))) {
            return null;
        }

        return { level, discount };
    };

    // 登录后请求一次用户等级权益，接口失败时不影响商品详情展示。
    loadUserLevel = async () => {
        if (!this.isUserLoggedIn()) {
            if (this.userLevelRequestStarted || this.state.userLevelInfo) {
                this.userLevelRequestStarted = false;
                this.userLevelRequestSequence += 1;
                this.setState({ userLevelInfo: null });
            }
            return;
        }
        if (this.userLevelRequestStarted) return;

        const requestSequence = ++this.userLevelRequestSequence;
        this.userLevelRequestStarted = true;

        try {
            const res = await this.props.user?.getUserLevel?.();
            if (requestSequence !== this.userLevelRequestSequence) return;

            this.setState({
                userLevelInfo: Number(res?.code) === 0 ? this.parseUserLevelInfo(res) : null,
            });
        } catch (error) {
            if (requestSequence !== this.userLevelRequestSequence) return;
            this.setState({ userLevelInfo: null });
        }
    };

    // 等级图标和折扣组成兑换商品统一展示的权益标签。
    renderLevelDiscountTag = (extraClassName = '', goods = this.props.goodsData) => {
        const { level, discount } = this.state.userLevelInfo || {};
        const levelBadge = level === undefined || level === null
            ? ''
            : getUserLevelBadge(level);
        // 原价与折扣价相等时没有实际折扣，隐藏所有位置的等级折扣标签。
        if (isGoodsDiscountPriceEqual(goods) || !levelBadge || discount === undefined || discount === null) {
            return null;
        }
        const levelTagClassName = Number(level) === 7
            ? styles.levelDiscountTagV7
            : styles.levelDiscountTagV6;

        return (
            <span className={classNames(styles.levelDiscountTag, levelTagClassName, extraClassName)}>
                <img
                    src={levelBadge}
                    alt={`V${level}`}
                    className={styles.levelDiscountBadge}
                />
                <span>{discount}折</span>
            </span>
        );
    };

    componentDidMount() {
        super.componentDidMount();
        // App 已挂载后进入详情页时也重新同步 Cookie，避免登录回跳后必须刷新页面。
        this.props.user?.hydrateLoginState?.();
        if ((this.props.location.state as any)?.openExchange) {
            this.props.history.replace({
                pathname: this.props.location.pathname,
                search: this.props.location.search,
                hash: this.props.location.hash,
                state: undefined,
            });
        }
        this.scheduleSaleBoundaryTimer();
        this.scheduleUnavailableRedirect();
        this.loadUserLevel();
        this.scrollCleanup = listenScrollDirection((scrollData: any) => {
            this.handleScroll(scrollData.direction);
        });
        window.addEventListener('scroll', this.handleGoodsListScroll, { passive: true });
        window.addEventListener('resize', this.updateWinningBarrageDuration);
        window.addEventListener('pagehide', this.cancelAppOpenFallback);
        window.addEventListener('blur', this.cancelAppOpenFallback);
        document.addEventListener('visibilitychange', this.handleAppOpenVisibilityChange);
        this.updateWinningBarrageDuration();
        this.handleScroll();
    }

    componentDidUpdate(prevProps: GoodsDetailH5Props) {
        // 用户登录信息由全局 Store 异步初始化，状态变化后补发等级权益请求。
        this.loadUserLevel();
        if (
            prevProps.goodsData?.code !== this.props.goodsData?.code
            || prevProps.goodsData?.list_at !== this.props.goodsData?.list_at
            || prevProps.goodsData?.delist_at !== this.props.goodsData?.delist_at
            || prevProps.goodsData?.launch_at !== this.props.goodsData?.launch_at
            || getLotteryEndAt(prevProps.goodsData) !== getLotteryEndAt(this.props.goodsData)
            || getExchangeEndAt(prevProps.goodsData) !== getExchangeEndAt(this.props.goodsData)
            || prevProps.goodsList !== this.props.goodsList
        ) {
            this.scheduleSaleBoundaryTimer();
            this.handleScroll();
        }
        // 下架空态等待推荐列表加载完成后，开始 3 秒自动跳转倒计时。
        if (
            prevProps.errorCode !== this.props.errorCode
            || prevProps.goodsListLoading !== this.props.goodsListLoading
            || prevProps.goodsList !== this.props.goodsList
        ) {
            this.scheduleUnavailableRedirect();
        }
        // 弹幕数量、文本或布局变化后，按最新实际宽度重新计算动画时长。
        this.updateWinningBarrageDuration();
    }

    componentWillUnmount() {
        this.scrollCleanup?.();
        this.scrollCleanup = null;
        window.removeEventListener('scroll', this.handleGoodsListScroll);
        window.removeEventListener('resize', this.updateWinningBarrageDuration);
        window.removeEventListener('pagehide', this.cancelAppOpenFallback);
        window.removeEventListener('blur', this.cancelAppOpenFallback);
        document.removeEventListener('visibilitychange', this.handleAppOpenVisibilityChange);
        this.cancelAppOpenFallback();
        this.userLevelRequestSequence += 1;
        this.userLevelRequestStarted = false;
        if (this.saleBoundaryTimer !== null) {
            window.clearTimeout(this.saleBoundaryTimer);
        }
        if (this.lotteryAnimationTimer !== null) {
            window.clearTimeout(this.lotteryAnimationTimer);
        }
        this.clearUnavailableRedirectTimer();
    }

    // 清理下架空态自动跳转定时器，避免点击或离开页面后重复跳转。
    clearUnavailableRedirectTimer = () => {
        if (this.unavailableRedirectTimer !== null) {
            window.clearTimeout(this.unavailableRedirectTimer);
            this.unavailableRedirectTimer = null;
        }
    };

    // 根据推荐列表决定下架空态的目标路由。
    getUnavailableRedirectTarget = () => {
        const firstGoodsCode = this.props.goodsList?.[0]?.code;
        const hasRecommendedGoods = firstGoodsCode !== undefined
            && firstGoodsCode !== null
            && Boolean(String(firstGoodsCode));

        return hasRecommendedGoods
            ? `/goodsDetail/${encodeURIComponent(String(firstGoodsCode))}`
            : '/';
    };

    // 处理下架空态按钮和定时器共用的跳转逻辑。
    redirectUnavailableGoods = () => {
        this.clearUnavailableRedirectTimer();
        this.props.history.replace(this.getUnavailableRedirectTarget());
    };

    // 推荐列表加载结束后，在下架空态停留 3 秒自动跳转。
    scheduleUnavailableRedirect = () => {
        this.clearUnavailableRedirectTimer();

        if (Number(this.props.errorCode) !== -4004 || this.props.goodsListLoading) {
            return;
        }

        this.unavailableRedirectTimer = window.setTimeout(() => {
            this.unavailableRedirectTimer = null;
            this.redirectUnavailableGoods();
        }, UNAVAILABLE_REDIRECT_DELAY);
    };

    // 记录弹幕可视区域，并在节点挂载后计算固定速度对应的动画周期。
    setWinningBarrageRef = (node: HTMLDivElement | null) => {
        this.winningBarrageRef = node;
        this.updateWinningBarrageDuration();
    };

    // 记录弹幕轨道，供滚动距离测量使用。
    setWinningBarrageTrackRef = (node: HTMLDivElement | null) => {
        this.winningBarrageTrackRef = node;
        this.updateWinningBarrageDuration();
    };

    // 按“容器宽度 + 轨道宽度”计算周期，确保单条和多条弹幕移动速度一致。
    updateWinningBarrageDuration = () => {
        const containerWidth = this.winningBarrageRef?.clientWidth || 0;
        const trackWidth = this.winningBarrageTrackRef?.scrollWidth || 0;
        if (!containerWidth || !trackWidth || !this.winningBarrageTrackRef) return;

        const distance = containerWidth + trackWidth;
        const duration = Math.round(
            distance / containerWidth * WINNING_BARRAGE_VIEWPORT_DURATION
        );
        this.winningBarrageTrackRef.style.setProperty(
            '--winning-barrage-start-offset',
            `${containerWidth}px`
        );
        this.winningBarrageTrackRef.style.animationDuration = `${duration}ms`;
    };

    getFixedNavOffset = (scrollDirection = this.state.scrollDirection) => {
        const downloadOffset = this.props.site?.appDownloadShowStatus ? 88 : 0;
        const source = new URLSearchParams(this.props.location.search).get('source');
        const headerVisible = Boolean(this.props.site?.showHeader)
            && source !== 'app'
            && (scrollDirection === 'up' || scrollDirection === null);

        return downloadOffset + (headerVisible ? 88 : 0);
    };

    // 原位导航进入 Header 下方的可视区域前，始终保留悬浮导航。
    handleScroll = (scrollDirection = this.state.scrollDirection) => {
        const navRect = this.goodsTabsRef?.getBoundingClientRect();
        const rootFontSize = Number.parseFloat(
            window.getComputedStyle(document.documentElement).fontSize
        ) || 0;
        const fixedTop = rootFontSize * (this.getFixedNavOffset(scrollDirection) / 100);
        const shouldShowFixed = navRect !== undefined && navRect.bottom <= fixedTop;

        if (
            this.state.showFixedNav !== shouldShowFixed
            || this.state.scrollDirection !== scrollDirection
        ) {
            this.setState({
                showFixedNav: shouldShowFixed,
                scrollDirection,
            });
        }
    };

    handleGoodsTabClick = (item: any) => {
        this.setState({ currentGoodsType: item.type });
        const params = item.goodsType
            ? { 'filter[goodsType]': item.goodsType }
            : {};
        this.goodsListPage = 1;
        this.goodsListParams = params;
        this.props.getGoodsList?.({ ...params, page: 1, perPage: GOODS_LIST_PAGE_SIZE });
    };

    // 推荐列表触底且未加载完全部数据时，按页请求下一页并追加展示。
    handleGoodsListScroll = () => {
        const { goodsList = [], goodsListTotal = 0, goodsListLoading } = this.props;
        const total = Number(goodsListTotal);
        if (
            !total
            || goodsList.length >= total
            || goodsListLoading
            || this.goodsListLoadingMore
        ) return;

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const viewportHeight = window.innerHeight;
        const documentHeight = Math.max(
            document.documentElement.scrollHeight,
            document.body?.scrollHeight || 0
        );
        if (scrollTop + viewportHeight < documentHeight - 120) return;

        const nextPage = this.goodsListPage + 1;
        this.goodsListLoadingMore = true;
        Promise.resolve(this.props.getGoodsList?.({
            ...this.goodsListParams,
            page: nextPage,
            perPage: GOODS_LIST_PAGE_SIZE,
        })).then((res: any) => {
            if (Number(res?.code) === 0) this.goodsListPage = nextPage;
        }).finally(() => {
            this.goodsListLoadingMore = false;
        });
    };

    // 在关键时间点自动刷新商品状态。
    scheduleSaleBoundaryTimer = () => {
        if (this.saleBoundaryTimer !== null) {
            window.clearTimeout(this.saleBoundaryTimer);
            this.saleBoundaryTimer = null;
        }

        const now = Date.now();
        const listAt = parseGoodsDateTime(this.props.goodsData?.list_at);
        const lotteryEndAt = getLotteryEndAt(this.props.goodsData);
        const exchangeEndAt = getExchangeEndAt(this.props.goodsData);
        // 列表商品进入倒计时、开抢或结束时，同步更新时间与库存显示。
        const goodsListBoundaryDelays = (this.props.goodsList || [])
            .filter(item => item.goods_stock > 0)
            .flatMap(item => {
                const startAt = Number(item.countdownTarget) || parseGoodsDateTime(item.list_at);
                const endAt = isLotteryGoods(item) ? getLotteryEndAt(item) : getExchangeEndAt(item);
                return [
                    ...(!item.saleStarted ? [startAt - now - ONE_HOUR, startAt - now] : []),
                    endAt - now,
                ];
            });
        const boundaryDelays = [
            listAt - now - ONE_HOUR,
            listAt - now,
            lotteryEndAt - now - ONE_HOUR,
            lotteryEndAt - now,
            exchangeEndAt - now,
            ...goodsListBoundaryDelays,
        ].filter(delay => delay > 0);
        if (boundaryDelays.length === 0) return;
        const delay = Math.min(...boundaryDelays);

        this.saleBoundaryTimer = window.setTimeout(() => {
            this.saleBoundaryTimer = null;
            this.setState(
                state => ({ saleTimingVersion: state.saleTimingVersion + 1 }),
                this.scheduleSaleBoundaryTimer
            );
        }, Math.min(delay, 2147483647));
    }

    handleSlideChange = (swiper: any) => {
        this.setState({ activeIndex: swiper.activeIndex });
    };

    handleGoodsSaleStart = () => {
        const goodsCode = this.props.goodsData?.code;
        if (goodsCode !== undefined && goodsCode !== null && String(goodsCode)) {
            this.props.onGoodsSaleStart?.(String(goodsCode));
        }
        this.setState(state => ({ saleTimingVersion: state.saleTimingVersion + 1 }));
    };

    // 生成 Android 新建日程 Intent，vivo 设备优先交给系统日历处理。
    getSaleReminderIntent = () => {
        const { goodsData } = this.props;
        const saleStartAt = parseGoodsDateTime(goodsData?.list_at);
        const saleActionText = getGoodsSaleActionText(goodsData);

        if (!Number.isFinite(saleStartAt) || saleStartAt <= Date.now()) return '';
        if (!/android/i.test(navigator.userAgent)) return '';

        const goodsName = String(goodsData?.goods_name || '商品');
        const reminderTitle = `${goodsName}开抢提醒`;
        const reminderDescription = `${goodsName}即将${saleActionText}，请及时参与。`;
        const encodeIntentExtra = (value: string) => encodeURIComponent(value);
        const isVivoDevice = /vivo/i.test(navigator.userAgent);

        // ACTION_INSERT 使用日程目录 MIME，不生成或下载 ICS 文件。
        return [
            'intent://com.android.calendar/events#Intent',
            'scheme=content',
            'action=android.intent.action.INSERT',
            'type=vnd.android.cursor.dir/event',
            ...(isVivoDevice ? ['package=com.bbk.calendar'] : []),
            `S.title=${encodeIntentExtra(reminderTitle)}`,
            `S.description=${encodeIntentExtra(reminderDescription)}`,
            `l.beginTime=${saleStartAt}`,
            `l.endTime=${saleStartAt + ONE_HOUR}`,
            'B.allDay=false',
            'end',
        ].join(';');
    };

    handleVCoinClick = () => {
        if (!this.props.user?.checkLogin('h5')) return;
        this.props.history.push('/vCoinRecord');
    };

    handlePrizeManagementClick = () => {
        if (!this.props.user?.checkLogin('h5')) return;
        this.props.history.push('/prizeManagement');
    };

    // 点击商品分享按钮直接复制分享文案，并在复制完成后提示结果。
    handleShareClick = (shareText: string) => {
        try {
            copy(shareText);
            Toast.success({ content: '链接复制成功，快去分享好友吧～' });
        } catch (error) {
            Toast.error({ content: '复制失败，请稍后重试' });
        }
    };

    // 本地余额可用时，先拦截 V 币不足场景。
    isVCoinInsufficient = () => {
        const { goodsData, user } = this.props;
        const balanceValue = user?.userInfo?.score ?? user?.userInfo?.total_score ?? user?.userInfo?.totalScore;
        const balance = Number(balanceValue);
        const payScore = Number(goodsData?.pay_score ?? goodsData?.goods_score ?? 0);

        return balanceValue !== undefined
            && balanceValue !== null
            && Number.isFinite(balance)
            && balance < payScore;
    };

    // 统一计算待开抢、已结束、售罄和已兑换状态。
    getSaleTimingState = () => {
        const { goodsData, goodsList = [] } = this.props;
        const goodsCode = goodsData?.code;
        const currentGoods = goodsCode === undefined || goodsCode === null
            ? undefined
            : goodsList.find(item => String(item.code) === String(goodsCode));
        const listAt = parseGoodsDateTime(goodsData?.list_at);
        const saleStarted = Boolean(currentGoods?.saleStarted) || (listAt > 0 && listAt <= Date.now());
        const isBeforeSale = !saleStarted && listAt > Date.now();
        const saleRemainingTime = isBeforeSale ? Math.max(listAt - Date.now(), 0) : 0;

        return { listAt, isBeforeSale, saleRemainingTime };
    };


    // 抽奖按钮和库存共用状态判断，支持计算列表商品。
    getLotteryButtonState = (goodsData = this.props.goodsData, saleTimingState = this.getSaleTimingState()) => {
        const { isBeforeSale, saleRemainingTime } = saleTimingState;
        const isSoldOut = Number(goodsData?.goods_stock) <= 0;
        const isLotteryEnded = isLotteryActivityEnded(goodsData);
        const hasQuota = Number(goodsData?.quota) !== 0;
        const hasRemainingQuota = goodsData?.remaining_quota !== undefined && goodsData?.remaining_quota !== null;
        const isExchanged = hasQuota && hasRemainingQuota && Number(goodsData.remaining_quota) === 0;

        const canDraw = !isBeforeSale && !isLotteryEnded && !isExchanged && Number(goodsData?.goods_stock) > 0;

        return { isBeforeSale, saleRemainingTime, isLotteryEnded, isSoldOut, isExchanged, canDraw };
    };

    // 兑换按钮和库存共用可兑换条件，与抽奖规则分开。
    getExchangeButtonState = (goodsData = this.props.goodsData, saleTimingState = this.getSaleTimingState()) => {
        const { isBeforeSale, saleRemainingTime } = saleTimingState;
        const isEnded = isExchangeActivityEnded(goodsData);
        const isSoldOut = Number(goodsData?.goods_stock) <= 0;
        const hasQuota = Number(goodsData?.quota) !== 0;
        const hasRemainingQuota = goodsData?.remaining_quota !== undefined && goodsData?.remaining_quota !== null;
        const isExchanged = hasQuota && hasRemainingQuota && Number(goodsData.remaining_quota) === 0;

        const canExchange = !isBeforeSale && !isEnded && !isExchanged && Number(goodsData?.goods_stock) > 0;

        return { isBeforeSale, saleRemainingTime, isEnded, isSoldOut, isExchanged, canExchange };
    };

    // 抽奖商品按钮专用流程：提醒、余额校验、抽奖。
    handleLotteryButtonClick = (event?: React.MouseEvent<HTMLAnchorElement>) => {
        const { user } = this.props;
        const { isBeforeSale, isLotteryEnded, isSoldOut, isExchanged } = this.getLotteryButtonState();

        if (isLotteryEnded || isSoldOut) {
            event?.preventDefault();
            return;
        }

        if (isExchanged) {
            event?.preventDefault();
            Toast.info({ content: '本商品抽奖次数已用完，看看其他商品吧' });
            return;
        }

        // 微信内置浏览器不支持系统日历 Intent，先引导用户到外部浏览器。
        if (isBeforeSale && /micromessenger/i.test(navigator.userAgent)) {
            event?.preventDefault();
            this.setState({ weChatBrowserGuideVisible: true });
            return;
        }

        if (!user?.checkLogin('h5')) {
            event?.preventDefault();
            return;
        }

        if (isBeforeSale) {
            if (!this.getSaleReminderIntent()) {
                event?.preventDefault();
                Toast.info({ content: '当前设备暂不支持直接设置系统日程' });
            }
            return;
        }

        event?.preventDefault();
        this.handleLotteryDraw();
    };

    // 兑换商品按钮专用流程：提醒、地址加载、打开兑换弹框。
    handleExchangeButtonClick = async (event?: React.MouseEvent<HTMLAnchorElement>) => {
        const {
            goodsData,
            user,
            getGoodsAddressList,
        } = this.props;
        const { isBeforeSale, isEnded, isSoldOut, isExchanged } = this.getExchangeButtonState();

        if (isEnded || isSoldOut) {
            event?.preventDefault();
            return;
        }

        if (isExchanged) {
            event?.preventDefault();
            Toast.info({ content: '本商品兑换次数已用完，看看其他商品吧' });
            return;
        }

        if (isBeforeSale && /micromessenger/i.test(navigator.userAgent)) {
            event?.preventDefault();
            this.setState({ weChatBrowserGuideVisible: true });
            return;
        }

        if (!user?.checkLogin('h5')) {
            event?.preventDefault();
            return;
        }

        if (isBeforeSale) {
            if (!this.getSaleReminderIntent()) {
                event?.preventDefault();
                Toast.info({ content: '当前设备暂不支持直接设置系统日程' });
            }
            return;
        }

        event?.preventDefault();

        if (Number(goodsData?.is_need_address) === 1) {
            const closeLoading = Toast.loading({ content: '加载中...' });
            const addressParams: Record<string, any> = { page: 1, perPage: 10 };
            const userId = user?.userInfo?.id ?? user?.userInfo?.userId;
            if (userId) addressParams['filter[userId]'] = userId;

            try {
                await getGoodsAddressList?.(addressParams);
            } finally {
                closeLoading?.();
            }
        }

        this.setState({ exchangePopupVisible: true });
    };

    showInsufficientPopup = () => {
        this.setState({
            exchangePopupVisible: false,
            insufficientPopupVisible: true,
        });
    };

    // 统一处理余额不足、未中奖、中奖和普通兑换结果。
    handleExchangeResult = (res: any) => {
        const message = String(res?.msg || '');
        const isInsufficient = /(?:V币|酷币|积分|余额).*(?:不足|不够)|(?:不足|不够).*(?:V币|酷币|积分|余额)/i.test(message);

        if (isLotteryGoods(this.props.goodsData) && Number(res?.code) === -14005) {
            this.setState({ lotteryAnimationVisible: false });
            this.showInsufficientPopup();
            return;
        }

        if (isInsufficient) {
            this.showInsufficientPopup();
            return;
        }

        if (isLotteryGoods(this.props.goodsData) && isLotteryNotWon(res)) {
            this.setState({
                exchangePopupVisible: false,
                lotteryEncouragementVisible: true,
                lotteryEncouragementMessage: getLotteryResultMessage(res),
                lotteryEncouragementIcon: getRandomLotteryEncouragementIcon(),
            });
            return;
        }

        if (res?.code === 0) {
            if (isLotteryGoods(this.props.goodsData)) {
                this.setState({
                    exchangePopupVisible: false,
                    lotteryWinVisible: true,
                    lotteryPrize: getLotteryPrizeResult(res, this.props.goodsData),
                });
                return;
            }

            this.setState({
                exchangePopupVisible: false,
                exchangeSuccessPopupVisible: true,
            });
            return;
        }

        // 商品已被抢光/售罄：先提示后端返回的文案；抽奖库存已由 store 直接同步。
        if (isOutOfStockResponse(res)) {
            Toast.error({ content: message || '商品已被抢光，看看其他好物吧~' });
            // 普通兑换仍需刷新详情和列表，抽奖流程不触发页面数据刷新。
            if (!isLotteryGoods(this.props.goodsData)) {
                this.props.refreshGoodsData?.();
            }
            return;
        }

        Toast.error({ content: message || '兑换失败，请稍后重试' });
    };

    // 处理兑换商品提交，权益类商品额外携带 IMEI 参数。
    executeExchange = async () => {
        const {
            goodsData,
            defaultAddress,
            exchangeGoods,
            exchangeLoading,
        } = this.props;
        const needAddress = Number(goodsData?.is_need_address) === 1;
        const goodsType = Number(goodsData?.goods_type ?? goodsData?.goodsType);
        const needImei = goodsType === GOODS_TYPE.RIGHTS;

        if (needAddress && !defaultAddress) {
            Toast.info({ content: '请选择收货地址' });
            return;
        }

        if (exchangeLoading) return;

        if (this.isVCoinInsufficient()) {
            this.showInsufficientPopup();
            return;
        }

        const goodsCode = goodsData?.code ?? this.props.goodsCode;
        if (goodsCode === undefined || goodsCode === null || !String(goodsCode)) {
            Toast.error({ content: '商品信息无效，请稍后重试' });
            return;
        }

        const addressValue = defaultAddress?.id ?? defaultAddress?.addressId ?? defaultAddress?.address_id;
        const addressId = needAddress ? Number(addressValue) : null;
        if (needAddress && !Number.isInteger(addressId)) {
            Toast.info({ content: '请选择收货地址' });
            return;
        }

        const imei = this.state.exchangeImei.trim();
        if (needImei && !imei) {
            Toast.info({ content: '请输入IMEI码' });
            return;
        }

        // 兑换接口接收商品 code 和地址 ID；权益类商品额外传递 IMEI。
        const exchangeParams: Record<string, any> = {
            code: String(goodsCode),
            addressId,
        };
        if (needImei) exchangeParams.imei = imei;
        const res = await exchangeGoods?.(exchangeParams);
        this.handleExchangeResult(res);
    };

    // 仅处理抽奖商品的抽奖提交，参数为商品 code。
    executeLotteryDraw = async () => {
        const { goodsData, lotteryDraw, lotteryLoading, user } = this.props;
        if (lotteryLoading || this.state.lotteryAnimationVisible) return;

        const goodsCode = goodsData?.code ?? this.props.goodsCode;
        if (goodsCode === undefined || goodsCode === null || !String(goodsCode)) {
            Toast.error({ content: '商品信息无效，请稍后重试' });
            return;
        }

        // 抽奖真正开始前再次校验 V 币余额，余额不足时只打开提示弹框，不提交抽奖请求。
        if (this.isVCoinInsufficient()) {
            this.showInsufficientPopup();
            return;
        }

        this.lotteryAnimationStartedAt = Date.now();
        this.setState({ lotteryAnimationVisible: true });
        const res = await lotteryDraw?.({ code: String(goodsCode) });

        // 每次抽奖请求完成后读取服务端最新用户信息，连续抽奖时同步更新左下角 V 币余额。
        const userCode = user?.userInfo?.code;
        if (userCode) {
            try {
                await user.fetchUserInfo({ code: userCode });
            } catch (error) {
                // 用户信息刷新失败不影响本次抽奖结果展示，下次抽奖后继续校准余额。
            }
        }

        if (Number(res?.code) === -14005) {
            const elapsed = Date.now() - this.lotteryAnimationStartedAt;
            const remaining = Math.max(LOTTERY_ANIMATION_DURATION - elapsed, 0);
            this.lotteryAnimationTimer = window.setTimeout(() => {
                this.lotteryAnimationTimer = null;
                this.setState({ lotteryAnimationVisible: false }, this.showInsufficientPopup);
            }, remaining);
            return;
        }

        const elapsed = Date.now() - this.lotteryAnimationStartedAt;
        const remaining = Math.max(LOTTERY_ANIMATION_DURATION - elapsed, 0);
        this.lotteryAnimationTimer = window.setTimeout(() => {
            this.lotteryAnimationTimer = null;
            this.setState({ lotteryAnimationVisible: false }, () => {
                this.handleExchangeResult(res);
            });
        }, remaining);
    };

    // 点击确定时先校验收货地址，缺失时只提示不提交兑换。
    handleConfirmExchange = () => {
        const { goodsData, defaultAddress } = this.props;
        const needAddress = Number(goodsData?.is_need_address) === 1;

        if (needAddress && !defaultAddress) {
            Toast.info({ content: '请选择收货地址' });
            return;
        }

        this.executeExchange();
    };

    // 记录权益类商品兑换所需的 IMEI 号码。
    handleImeiChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ exchangeImei: event.target.value });
    };

    handleLotteryDraw = () => {
        this.executeLotteryDraw();
    };

    // 抽奖时先展示背景射线发散旋转，再让礼盒快速晃动。
    renderLotteryAnimation = () => {
        if (!this.state.lotteryAnimationVisible) return null;

        return (
            <div className={styles.lotteryAnimationOverlay} role="status" aria-label="正在抽奖">
                <div className={styles.lotteryAnimationStage}>
                    {/* 射线背景层：先放大，再在礼盒后方持续旋转。 */}
                    <div className={styles.lotteryAnimationRays} aria-hidden="true">
                        <img
                            src={`${BASE_IMG_URL}/goods/bg2.png`}
                            alt=""
                            draggable={false}
                            className={styles.lotteryAnimationRaysRotator}
                        />
                    </div>
                    {/* 礼盒主体层：使用统一礼盒图标，放大出现后快速上下左右晃动。 */}
                    <img
                        src={`${BASE_IMG_URL}/goods/icon4.png`}
                        alt="抽奖礼盒"
                        draggable={false}
                        className={styles.lotteryGiftBox}
                    />
                </div>
            </div>
        );
    };

    handleLotteryEncouragementReturn = () => {
        // 返回详情页时仅关闭未中奖弹框，不刷新商品详情数据。
        this.setState({ lotteryEncouragementVisible: false });
    };

    // 重抽时仅关闭结果弹框并调用抽奖接口，不刷新商品详情页面。
    handleLotteryRetry = () => {
        this.setState({ lotteryEncouragementVisible: false }, () => {
            this.handleLotteryButtonClick();
        });
    };

    renderLotteryEncouragementPopup = () => {
        const payScore = this.props.goodsData?.pay_score ?? this.props.goodsData?.goods_score ?? 0;

        return (
            <CommonPopupNew variant="h5" desktopMode={this.props.desktopMode}
                visible={this.state.lotteryEncouragementVisible}
                isShowTitle={false}
                isShowConfirm={false}
                isShowCancel={false}
                popupContentClassName={this.state.lotteryEncouragementVisible
                    ? styles.lotteryResultPopup
                    : undefined}
                onCancelChange={this.handleLotteryEncouragementReturn}
            >
                {/* 未中奖结果及重新抽奖操作。 */}
                <div className={styles.lotteryEncouragementContent}>
                    <img
                        src={this.state.lotteryEncouragementIcon}
                        alt=""
                        className={styles.lotteryEncouragementMascot}
                    />
                    <div className={styles.lotteryEncouragementText}>
                        {this.state.lotteryEncouragementMessage}
                    </div>
                    <div className={styles.lotteryEncouragementActions}>
                        <div className={styles.lotteryRetryButton} onClick={this.handleLotteryRetry}>
                            <div>再抽一次</div>
                            <div className={styles.lotteryRetryCost}>{payScore}V币/次</div>
                        </div>
                        <div
                            className={styles.lotteryReturnButton}
                            onClick={this.handleLotteryEncouragementReturn}
                        >
                            返回
                        </div>
                    </div>
                </div>
            </CommonPopupNew>
        );
    };

    // 前往奖品管理的“我的奖品”标签领取奖品。
    handleClaimLotteryPrize = () => {
        this.setState({ lotteryWinVisible: false });
        this.props.history.push('/prizeManagement?tab=prizes');
    };

    handleCloseLotteryWinPopup = () => {
        this.setState({ lotteryWinVisible: false });
    };

    renderLotteryWinPopup = () => {
        const prize = this.state.lotteryPrize || {};

        return (
            <CommonPopupNew variant="h5" desktopMode={this.props.desktopMode}
                visible={this.state.lotteryWinVisible}
                isShowTitle={false}
                isShowConfirm={false}
                isShowCancel={false}
                popupContentClassName={this.state.lotteryWinVisible
                    ? styles.lotteryResultPopup
                    : undefined}
                onCancelChange={this.handleCloseLotteryWinPopup}
                childrenSlotStyle={{paddingLeft: px2rem(36), paddingRight: px2rem(36), paddingTop: 0}}
            >
                {/* 中奖商品、提示文案及领取入口。 */}
                <div className={styles.lotteryWinContent}>
                    {prize.image ? (
                        <img className={styles.lotteryWinImage} src={prize.image} alt={prize.name || '中奖奖品'} />
                    ) : (
                        <img className={styles.lotteryWinImagePlaceholder} src={`${BASE_IMG_URL}/goods/icon4.png`} alt={prize.name || '中奖奖品'} />
                    )}
                    <div className={styles.lotteryWinTitle}>中奖啦</div>
                    <div className={styles.lotteryWinText}>
                        恭喜你抽中1份 {prize.name}！
                        请在{prize.validDays}天内填写收货地址，逾期将取消中奖资格。
                    </div>
                    <div className={styles.lotteryClaimButton} onClick={this.handleClaimLotteryPrize}>
                        去领取
                    </div>
                </div>
            </CommonPopupNew>
        );
    };

    handleOpenVCoinMall = () => {
        // 微信会拦截自定义 deeplink，在当前页改为浏览器打开引导。
        if (/micromessenger/i.test(navigator.userAgent)) {
            this.setState({
                insufficientPopupVisible: false,
                weChatBrowserGuideVisible: true,
            });
            return;
        }

        this.setState({ insufficientPopupVisible: false });
        this.cancelAppOpenFallback();
        // 保留用户点击手势，立即交给系统弹出“打开 vivo 社区”确认框。
        window.location.href = V_COIN_MALL_APP_URL;
        // 两秒后页面仍未离开，视为 App 未安装，直接跳应用商店下载地址。
        this.appOpenFallbackTimer = window.setTimeout(() => {
            this.appOpenFallbackTimer = null;
            window.location.href = APP_DOWNLOAD_URL;
        }, APP_OPEN_FALLBACK_DELAY);
    };

    cancelAppOpenFallback = () => {
        if (this.appOpenFallbackTimer !== null) {
            window.clearTimeout(this.appOpenFallbackTimer);
            this.appOpenFallbackTimer = null;
        }
    };

    handleAppOpenVisibilityChange = () => {
        if (document.hidden) this.cancelAppOpenFallback();
    };

    renderInsufficientPopup = () => (
        <CommonPopupNew variant="h5" desktopMode={this.props.desktopMode}
            visible={this.state.insufficientPopupVisible}
            isShowTitle={false}
            confirmText="打开App端"
            cancelText="取消"
            popupContentBg={true}
            onCancelChange={() => this.setState({ insufficientPopupVisible: false })}
            onConfirmButtonChange={this.handleOpenVCoinMall}
        >
            {/* V币不足或普通兑换失败提示。 */}
            <div className={styles.exchangeFailureContent}>
                {!isLotteryGoods(this.props.goodsData) && (
                    <img
                        src={`${BASE_IMG_URL}/goods/icon12.png`}
                        alt="兑换失败"
                        className={styles.exchangeFailureIcon}
                    />
                )}
                <div className={styles.exchangeFailureTitle}>
                    {isLotteryGoods(this.props.goodsData) ? 'V币不足' : '兑换失败'}
                </div>
                <div className={styles.exchangeFailureText}>
                    您的V币余额不足，请前往vivo社区App端【V币商城】赚足V币后再来兑换吧
                </div>
            </div>
        </CommonPopupNew>
    );

    // 兑换完成后同步刷新商品和用户数据。
    refreshAfterExchange = () => {
        const { refreshGoodsData, user } = this.props;
        const refreshTasks: any[] = [];
        const goodsTask = refreshGoodsData?.();
        if (goodsTask) refreshTasks.push(goodsTask);
        if (user?.userInfo?.code) {
            const userTask = user.fetchUserInfo({ code: user.userInfo.code });
            if (userTask) refreshTasks.push(userTask);
        }
        return Promise.all(refreshTasks);
    };

    handleCloseExchangeSuccess = () => {
        this.setState({ exchangeSuccessPopupVisible: false });
        this.refreshAfterExchange();
    };

    handleViewExchangeRecord = () => {
        this.setState({ exchangeSuccessPopupVisible: false });
        this.refreshAfterExchange();
        this.props.history.push('/prizeManagement?tab=records');
    };

    renderExchangeSuccessPopup = () => (
        <CommonPopupNew variant="h5" desktopMode={this.props.desktopMode}
            visible={this.state.exchangeSuccessPopupVisible}
            isShowTitle={false}
            confirmText="查看详情"
            cancelText="取消"
            popupContentBg={true}
            onCancelChange={this.handleCloseExchangeSuccess}
            onConfirmButtonChange={this.handleViewExchangeRecord}
        >
            {/* 普通商品兑换成功提示。 */}
            <div className={styles.exchangeSuccessContent}>
                <img
                    src={`${BASE_IMG_URL}/goods/icon6.png`}
                    alt="兑换成功"
                    className={styles.exchangeSuccessIcon}
                />
                <div className={styles.exchangeSuccessTitle}>兑换成功</div>
                <div className={styles.exchangeSuccessText}>可前往奖品管理查看兑换记录</div>
            </div>
        </CommonPopupNew>
    );

    renderExchangePopup = () => {
        const { goodsData, defaultAddress, exchangeLoading } = this.props;
        const { exchangePopupVisible } = this.state;
        if (!exchangePopupVisible) return null;

        const goodsImage = goodsData?.goods_images?.[0]?.imgurl || '';
        const payScore = getGoodsPayScore(goodsData);
        const originalScore = getGoodsOriginalScore(goodsData);
        const showOriginalScore = hasGoodsOriginalScore(goodsData);
        const needAddress = Number(goodsData?.is_need_address) === 1;
        const needImei = Number(goodsData?.goods_type ?? goodsData?.goodsType) === GOODS_TYPE.RIGHTS;
        const region = defaultAddress
            ? `${defaultAddress.detail_address || defaultAddress.address || ''}`
            : '';
        const receiverName = defaultAddress?.true_name || defaultAddress?.name || defaultAddress?.receiver || '';
        const receiverPhone = defaultAddress?.phone || defaultAddress?.mobile || '';

        return (
            <CommonPopup variant="h5" desktopMode={this.props.desktopMode}
                visible={exchangePopupVisible}
                title="兑换商品"
                isShowTitle={false}
                isShowConfirm={false}
                childrenSlotStyle={{paddingTop: px2rem(12)}}
                isShowCancel={false}
                onCancelChange={() => this.setState({ exchangePopupVisible: false })}
            >
                <div className={styles.exchangeGoodsInfoBox}>
                    <div className={styles.exchangeGoodsInfoTitle}>兑换商品</div>
                    {/* 待兑换商品的图片、名称和所需V币。 */}
                    <div className={styles.exchangeGoodsInfo}>
                        <img className={styles.exchangeGoodsImage} src={goodsImage} alt="" />
                        <div className={styles.exchangeGoodsContent}>
                            <div className={styles.exchangeGoodsName}>
                                {/* {goodsData?.isLottery ? '【抽奖】' : ''}{goodsData?.goods_name} */}
                                {this.renderLevelDiscountTag()}
                                {goodsData?.goods_name}
                            </div>
                            <div className={styles.exchangeGoodsScore}>
                                <img src={`${BASE_IMG_URL}/goods/icon2.png`} alt="" className={styles.coinIcon} />
                                <div>{payScore}</div>
                                {showOriginalScore && (
                                    <div className={styles.originalPrice}>{originalScore}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 实物商品的收货地址选择区域。 */}
                    {needAddress && (
                        <div
                            className={styles.exchangeAddress}
                            onClick={() => {
                                this.setState({ exchangePopupVisible: false });
                                const goodsCode = goodsData?.code ?? this.props.goodsCode;
                                this.props.history.push(`/prizeManagement?tab=address&select=1&goodsCode=${encodeURIComponent(String(goodsCode))}`);
                            }}
                        >
                            {defaultAddress ? (
                                <>
                                    {/* 已有地址时展示完整收货信息和进入箭头。 */}
                                    <img src={`${BASE_IMG_URL}/goods/icon9.png`} alt="" className={styles.addressIcon} />
                                    <div className={styles.addressContent}>
                                        <div className={styles.addressDetail}>{region}</div>
                                        <div className={styles.addressReceiver}>{receiverName} {receiverPhone}</div>
                                    </div>
                                    <img src={`${BASE_IMG_URL}/goods/jtr2.png`} alt="" className={styles.addressArrow} />
                                </>
                            ) : (
                                <>
                                    {/* 无地址时按设计稿居中展示 icon3 和选择提示。 */}
                                    <div className={styles.selectAddressContent}>
                                        <img
                                            src={`${BASE_IMG_URL}/goods/icon3.png`}
                                            alt=""
                                            className={classNames(styles.addressIcon, styles.selectAddressIcon)}
                                        />
                                        <div className={styles.selectAddress}>选择收货地址</div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* 权益类商品需要填写 IMEI，用于兑换对应的权益。 */}
                    {needImei && (
                        <div className={styles.imeiField}>
                            <label className={styles.imeiLabel} htmlFor="goods-exchange-imei">IMEI码</label>
                            <input
                                id="goods-exchange-imei"
                                className={styles.imeiInput}
                                value={this.state.exchangeImei}
                                onChange={this.handleImeiChange}
                                placeholder="在拨号界面输入*#06#查看"
                                inputMode="numeric"
                                maxLength={20}
                            />
                        </div>
                    )}

                    {/* 兑换规则和地址确认提示。 */}
                    <div className={styles.exchangeTips}>
                        <div>温馨提示：</div>
                        <div>1. V币一经兑换，概不退还</div>
                        <div>2. 兑换成功后，地址不能修改，请认真核对地址</div>
                    </div>

                    {/* 兑换弹框的确定与取消操作。 */}
                    <div className={styles.exchangeActions}>
                        <div
                            className={`${styles.exchangeConfirm} ${needAddress && !defaultAddress ? styles.exchangeConfirmEmpty : ''}`}
                            onClick={this.handleConfirmExchange}
                        >
                            {exchangeLoading ? '兑换中...' : '确定'}
                        </div>
                        <div
                            className={styles.exchangeCancel}
                            onClick={() => this.setState({ exchangePopupVisible: false })}
                        >
                            取消
                        </div>
                    </div>
                </div>
            </CommonPopup>
        );
    };

    // 商品不存在或已下架时，根据推荐列表展示继续浏览或返回首页入口。
    renderUnavailableGoods = () => {
        const { goodsList = [] } = this.props;
        const firstGoodsCode = goodsList[0]?.code;
        const hasRecommendedGoods = firstGoodsCode !== undefined
            && firstGoodsCode !== null
            && Boolean(String(firstGoodsCode));
        const description = hasRecommendedGoods
            ? '该商品已下架，为你推荐其它商品'
            : '该商品已下架';

        return (
            <div className={classNames(
                styles.unavailableState,
                this.props.desktopMode && styles.desktopMode,
            )}>
                {/* 沿用奖品管理空列表的图标及提示文字样式。 */}
                <img
                    className={styles.unavailableIllustration}
                    src={`${BASE_IMG_URL}/common/null.png`}
                    alt=""
                />
                <p className={styles.unavailableText}>{description}</p>
                <button
                    type="button"
                    className={styles.unavailableButton}
                    onClick={this.redirectUnavailableGoods}
                >
                    {hasRecommendedGoods ? '去看看' : '返回社区首页'}
                </button>
            </div>
        );
    };

    render() {
        const {
            goodsCode,
            goodsData,
            loading,
            error,
            errorCode,
            goodsList = [],
            goodsListLoading,
            onGoodsSaleStart,
            site,
            user,
        } = this.props;
        const { activeIndex, currentGoodsType, showFixedNav } = this.state;
        const isGoodsUnavailable = Number(errorCode) === -4004;

        // 下架状态需要等待推荐列表加载完成，再决定“去看看”或返回首页。
        if (loading || (isGoodsUnavailable && goodsListLoading)) {
            return <div className={styles.pageStatus}>商品加载中...</div>;
        }

        if (isGoodsUnavailable) {
            return this.renderUnavailableGoods();
        }

        if (error || !goodsData) {
            return <div className={styles.pageStatus}>{error || '暂无商品信息'}</div>;
        }

        const parserOptions = {
            replace: (domNode: any) => {
                // 可以在这里自定义节点替换逻辑
            },
        };
        const isLotteryProduct = isLotteryGoods(goodsData);
        const shareGoodsCode = goodsData?.code ?? goodsCode;
        const shareUrl = `https://club.vivo.com.cn/goodsDetail/${encodeURIComponent(String(shareGoodsCode || ''))}`;
        const shareGoodsName = goodsData.goods_name || '商品详情';
        const sharePayScore = getGoodsPayScore(goodsData);
        // 分享标题按兑换/抽奖类型使用不同业务文案，价格取实际消耗的 pay_score。
        const shareTitle = isLotteryProduct
            ? `【抽奖】${shareGoodsName}，消耗${sharePayScore}V币抽一次。上vivo社区互动攒V币，赢专属V粉周边~`
            : `【兑换】${shareGoodsName}，消耗${sharePayScore}V币兑换。上vivo社区互动攒V币，赢专属V粉周边~`;
        const shareText = `${shareTitle}\r\n${shareUrl}\r\n复制本消息打开vivo社区App即可查看详情~`;
        const goodsImages = goodsData?.goods_images || [];
        const winningBarrage = isLotteryProduct && Array.isArray(goodsData?.lottery?.winningBarrage)
            ? goodsData.lottery.winningBarrage
            : [];
        const currentGoodsTypeIndex = Math.max(
            GOODS_TABS.findIndex(item => item.type === currentGoodsType),
            0
        );
        // 按服务端校准后的目标时间计算剩余时长，避免一直使用列表加载时的旧值。
        const now = Date.now();
        // 过滤掉当前正在浏览的商品，避免推荐列表出现重复项。
        // 判空与字符串化都是循环不变量，提到循环外计算一次即可；
        // 用 reduce 合并 filter + map 为单次遍历，避免产生中间数组。
        const currentGoodsCodeRaw = goodsData?.code ?? goodsCode;
        const currentGoodsCodeKey = currentGoodsCodeRaw === undefined
            || currentGoodsCodeRaw === null
            || currentGoodsCodeRaw === ''
            ? null
            : String(currentGoodsCodeRaw);
        const recommendedGoods = goodsList.reduce<Array<Record<string, any>>>((acc, item) => {
            if (currentGoodsCodeKey !== null && String(item.code) === currentGoodsCodeKey) {
                return acc;
            }
            const listAt = Number(item.countdownTarget) || parseGoodsDateTime(item.list_at);
            const remainingTime = Math.max(listAt - now, 0);
            const isLottery = isLotteryGoods(item);
            const endAt = isLottery ? getLotteryEndAt(item) : getExchangeEndAt(item);
            const endRemainingTime = Math.max(endAt - now, 0);
            // 活动到期：抽奖/兑换均展示"已结束"。
            const isActivityEnded = endAt > 0 && endRemainingTime <= 0;
            // 库存耗尽：兑换类才使用"已售罄"，抽奖类没有售罄概念，退化为"已结束"。
            const isSoldOut = !isLottery && Number(item.goods_stock) <= 0;
            // 兼容旧字段：任何终止态都归入 isEnded，用于控制置灰、图标切换等展示。
            const isEnded = isActivityEnded || isSoldOut || (isLottery && Number(item.goods_stock) <= 0);
            // 标签文案：抽奖类只有"已结束"；兑换类优先"已售罄"，其次"已结束"。
            let endedTagText = '';
            if (isLottery) {
                endedTagText = isEnded ? '已结束' : '';
            } else if (isSoldOut) {
                endedTagText = '已售罄';
            } else if (isActivityEnded) {
                endedTagText = '已结束';
            }
            const timingState = {
                listAt,
                isBeforeSale: !item.saleStarted && remainingTime > 0,
                saleRemainingTime: remainingTime,
            };
            const showStock = isLottery
                ? this.getLotteryButtonState(item, timingState).canDraw
                : this.getExchangeButtonState(item, timingState).canExchange;

            acc.push({ ...item, listAt, remainingTime, endAt, endRemainingTime, isEnded, endedTagText, showStock });
            return acc;
        }, []);
        const saleTimingState = this.getSaleTimingState();
        const lotteryButtonState = this.getLotteryButtonState();
        const exchangeButtonState = this.getExchangeButtonState();
        const { listAt: saleStartAt, isBeforeSale, saleRemainingTime } = saleTimingState;
        const saleActionText = getGoodsSaleActionText(goodsData);
        const canShowSaleTiming = !isLotteryProduct || !lotteryButtonState.isLotteryEnded;
        const saleReminderIntent = isBeforeSale && canShowSaleTiming ? this.getSaleReminderIntent() : '';
        const showSaleStartDateTime = canShowSaleTiming
            && isBeforeSale
            && saleStartAt > 0
            && saleRemainingTime > ONE_HOUR;
        const showSaleStartCountdown = canShowSaleTiming
            && isBeforeSale
            && saleStartAt > 0
            && saleRemainingTime > 0
            && saleRemainingTime <= ONE_HOUR;
        const saleStartDateTimeParts = showSaleStartDateTime
            ? getMonthDayTimeParts(saleStartAt)
            : null;
        const lotteryEndAt = getLotteryEndAt(goodsData);
        const lotteryEndRemaining = lotteryEndAt - Date.now();
        const showLotteryEndDateTime = isLotteryProduct
            && !lotteryButtonState.isLotteryEnded
            && !isBeforeSale
            && lotteryEndAt > 0
            && lotteryEndRemaining > ONE_HOUR;
        const showLotteryEndCountdown = isLotteryProduct
            && !lotteryButtonState.isLotteryEnded
            && !isBeforeSale
            && lotteryEndAt > 0
            && lotteryEndRemaining > 0
            && lotteryEndRemaining <= ONE_HOUR;
        const lotteryEndDateTimeParts = showLotteryEndDateTime
            ? getMonthDayTimeParts(lotteryEndAt)
            : null;
        const isLotteryInProgress = isLotteryProduct && lotteryButtonState.canDraw;
        const goodsType = Number(goodsData?.goods_type ?? goodsData?.goodsType);
        const isUserLoggedIn = this.isUserLoggedIn();
        const userScoreValue = user?.userInfo?.score
            ?? user?.userInfo?.total_score
            ?? user?.userInfo?.totalScore;
        const userScore = Number(userScoreValue);
        const exchangePayScore = Number(getGoodsPayScore(goodsData));
        const exchangeQuota = Number(goodsData?.remaining_quota);
        const hasExchangeQuota = Number.isFinite(exchangeQuota) && exchangeQuota > 0;
        const scoreBasedExchangeQuota = Number.isFinite(userScore)
            && userScore >= 0
            && Number.isFinite(exchangePayScore)
            && exchangePayScore > 0
            ? Math.floor(userScore / exchangePayScore)
            : 0;
        // 有后台限额时，剩余次数取后台 quota 和用户积分可兑换次数中的较小值。
        const remainingExchangeQuota = hasExchangeQuota
            ? Math.min(Math.floor(exchangeQuota), scoreBasedExchangeQuota)
            : 0;
        // 未配置 quota 时不展示剩余兑换次数。
        const showPhysicalExchangeQuota = !isLotteryProduct
            && goodsType === GOODS_TYPE.PHYSICAL
            && exchangeButtonState.canExchange
            && isUserLoggedIn
            && hasExchangeQuota
            && Number.isFinite(userScore)
            && userScore >= 0
            && Number.isFinite(exchangePayScore)
            && exchangePayScore > 0;
        // 兑换次数用完不影响库存展示；只要商品未结束且仍有库存，详情顶部继续显示库存。
        const showGoodsStock = isLotteryProduct
            ? lotteryButtonState.canDraw
            : !exchangeButtonState.isEnded
                && !exchangeButtonState.isSoldOut
                && Number(goodsData?.goods_stock) > 0;
        // 商品尚未开始时，库存位置统一提示“即将开抢”，不展示实际库存或结束状态。
        const isGoodsBeforeSale = isBeforeSale;
        const lotteryParticipantCount = getLotteryParticipantCount(goodsData);
        const showLotteryParticipantCount = isLotteryProduct
            && Number.parseFloat(String(lotteryParticipantCount)) > 0;
        const lotteryPayScore = getGoodsPayScore(goodsData);
        const originalScore = getGoodsOriginalScore(goodsData);
        const showOriginalScore = !isLotteryProduct && hasGoodsOriginalScore(goodsData);
        const buttonText = isLotteryProduct
            ? lotteryButtonState.isLotteryEnded
                ? '抽奖活动已结束'
                : lotteryButtonState.isSoldOut
                    ? '抽奖活动已结束'
                    : lotteryButtonState.isExchanged
                        ? '已兑换'
                        : lotteryButtonState.isBeforeSale
                            ? '设置开抢提醒'
                            : '点击抽奖'
            : exchangeButtonState.isSoldOut
                ? '已售罄'
                : exchangeButtonState.isEnded
                    ? '兑换活动已结束'
                : exchangeButtonState.isExchanged
                    ? '已兑换'
                : exchangeButtonState.isBeforeSale
                        ? '设置开抢提醒'
                        : '立即兑换';
        // 仅在可立即兑换状态下，将当前用户的等级折扣放到按钮主文案右侧。
        const showExchangeDiscountButton = !isLotteryProduct && exchangeButtonState.canExchange;
        const buttonClickHandler = isLotteryProduct
            ? this.handleLotteryButtonClick
            : this.handleExchangeButtonClick;
        const buttonIsActive = isLotteryProduct
            ? !lotteryButtonState.isLotteryEnded && !lotteryButtonState.isSoldOut && !lotteryButtonState.isExchanged
            : !exchangeButtonState.isEnded && !exchangeButtonState.isSoldOut && !exchangeButtonState.isExchanged;
        // 抽奖动画期间，主按钮切换为不可点击的“锦鲤附体”状态，避免重复提交抽奖请求。
        const isLotteryDrawing = isLotteryProduct && this.state.lotteryAnimationVisible;
        const buttonClassName = classNames(
            styles.exchangeButton,
            buttonIsActive && !isLotteryDrawing && styles.exchangeButtonActive,
            isLotteryDrawing && styles.lotteryDrawingButton,
        );

        const hasLotteryTimingInfo = showSaleStartDateTime
            || showSaleStartCountdown
            || showLotteryEndDateTime
            || showLotteryEndCountdown;

        return (
            <div
                className={classNames(
                    styles.page,
                    hasLotteryTimingInfo && styles.pageWithLotteryTiming,
                    this.props.desktopMode && styles.desktopMode,
                )}
                data-goods-code={goodsCode}
            >
                {/* 商品主信息：轮播图、中奖弹幕、价格及库存。 */}
                <div className={styles.goodsCard}>
                    <div className={styles.imageArea}>
                        {/* 商品图片轮播区域。 */}
                        <Swiper
                            modules={[Navigation, Pagination, Autoplay]}
                            autoplay={false}
                            loop={false}
                            onSlideChange={this.handleSlideChange}
                            className={styles.goodsSwiper}
                        >
                            {goodsImages.map((image: any, index: number) => (
                                <SwiperSlide key={image.id || image.imgurl}>
                                    <div className={styles.slideItem}>
                                        <img
                                            className={styles.goodsImage}
                                            src={image.imgurl}
                                            alt={`${goodsData.goods_name} 商品图 ${index + 1}`}
                                            loading={index === 0 ? 'eager' : 'lazy'}
                                        />
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        {/* 抽奖商品的中奖弹幕，按接口顺序循环滚动。 */}
                        {winningBarrage.length > 0 && (
                            <div className={styles.winningBarrage} ref={this.setWinningBarrageRef}>
                                <div
                                    className={styles.winningBarrageTrack}
                                    ref={this.setWinningBarrageTrackRef}
                                >
                                    {winningBarrage.map((item: any, index: number) => (
                                        <div
                                            className={styles.winningBarrageItem}
                                            key={`${item.createdAt || item.nickname || 'winning'}-${index}`}
                                        >
                                            <Avatar
                                                variant="h5"
                                                avatarUrl={item.avatar}
                                                size={36}
                                                showOfficialTag={true}
                                                officialSize={10}
                                                titleMark={item.titleMark}
                                            />
                                            <span className={styles.winningBarrageText}>恭喜 {item.nickname || '用户'} 中奖
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {goodsImages.length > 0 && (
                            <div className={styles.imageCount}>{activeIndex + 1}/{goodsImages.length}</div>
                        )}
                    </div>

                    {/* 商品V币价格、参与人次、库存和名称。 */}
                    <div className={styles.goodsInfo}>
                        <div className={styles.priceRow}>
                            <div className={styles.price}>
                                <img src={`${BASE_IMG_URL}/goods/icon2.png`} alt="" className={styles.coinIcon} />
                                <div className={styles.priceNumber}>{lotteryPayScore}</div>
                                {/* 抽奖商品价格按单次抽奖展示单位。 */}
                                {isLotteryProduct && <span className={styles.priceUnit}>/次</span>}
                                {showOriginalScore && (
                                    <div className={styles.originalPrice}>{originalScore}</div>
                                )}
                            </div>
                            {/* 商品未开始时只提示即将开抢，不展示参与人次和库存。 */}
                            {isGoodsBeforeSale ? (
                                <div className={styles.stock}>即将开抢</div>
                            ) : isLotteryProduct ? (
                                // 抽奖类：参与人次大于 0 才展示，仅在可抽奖时追加库存。
                                (showLotteryParticipantCount || showGoodsStock) ? (
                                    <div className={styles.stock}>
                                        {/* {showLotteryParticipantCount && (
                                            <span>参与人次 {formatLotteryParticipantCount(lotteryParticipantCount)}</span>
                                        )} */}
                                        {showGoodsStock && (
                                            <>
                                                {/* {showLotteryParticipantCount && <span className={styles.stockSeparator} aria-hidden="true" />} */}
                                                <span>库存 {goodsData.goods_stock}</span>
                                            </>
                                        )}
                                    </div>
                                ) : null
                            ) : (
                                // 兑换类：售罄/活动结束优先展示状态角标，已兑换但有库存时仍展示库存。
                                exchangeButtonState.isSoldOut ? (
                                    <div className={styles.statusTag}>已售罄</div>
                                ) : exchangeButtonState.isEnded ? (
                                    <div className={styles.statusTag}>已结束</div>
                                ) : showGoodsStock ? (
                                    <div className={styles.stock}>
                                        <span>库存 {goodsData.goods_stock}</span>
                                    </div>
                                ) : exchangeButtonState.isExchanged ? (
                                    <div className={styles.statusTag}>已兑换</div>
                                ) : null
                            )}
                        </div>
                        <div className={styles.goodsNameRow}>
                            <div className={styles.goodsName}>
                                {/* 兑换商品名称前展示当前用户等级和商城折扣。 */}
                                {!isLotteryProduct && this.renderLevelDiscountTag()}
                                {goodsData.goods_name}
                            </div>
                            {/* 商品详情右侧分享入口，点击直接复制当前商品分享文案。 */}
                            <div
                                className={styles.goodsShareEnter}
                                role="button"
                                tabIndex={0}
                                onClick={() => this.handleShareClick(shareText)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        this.handleShareClick(shareText);
                                    }
                                }}
                            >
                                <img
                                    src={`${BASE_IMG_URL}/product/icon5.png`}
                                    alt=""
                                    className={styles.goodsShareIcon}
                                />
                                <span className={styles.goodsShareText}>分享</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 礼品详情使用富文本渲染接口内容。 */}
                <div className={styles.detailCard}>
                    <div className={styles.detailTitle}>礼品详情</div>
                    <div className={styles.richtext}>
                        {parse(goodsData.goods_intro || '', parserOptions)}
                    </div>
                </div>

                {/* 页面内导航离开可视区域后展示悬浮导航。 */}
                {!this.props.desktopMode && showFixedNav && (
                    <div
                        className={styles.fixedGoodsTabs}
                        style={{ top: px2rem(this.getFixedNavOffset()) }}
                    >
                        <TabNav variant="h5"
                            navList={GOODS_TABS}
                            currentIndex={currentGoodsTypeIndex}
                            boxStylesData={{ background: '#fff' }}
                            navClick={this.handleGoodsTabClick}
                        />
                    </div>
                )}

                {/* 推荐商品分类导航及分页商品列表。 */}
                <div className={styles.recommendCard}>
                    <div
                        className={styles.goodsTabs}
                        ref={(ref) => { this.goodsTabsRef = ref; }}
                    >
                        <TabNav variant="h5"
                            navList={GOODS_TABS}
                            currentIndex={currentGoodsTypeIndex}
                            boxStylesData={{ background: 'transparent' }}
                            navClick={this.handleGoodsTabClick}
                        />
                    </div>

                    <div className={styles.goodsList}>
                        {goodsListLoading && <div className={styles.goodsListStatus}>商品加载中...</div>}
                        {/* 推荐商品卡片，点击后进入对应商品详情。 */}
                        {recommendedGoods.map(item => (
                            <div
                                key={item.code}
                                className={styles.goodsItem}
                                onClick={() => this.props.history.push(`/goodsDetail/${encodeURIComponent(String(item.code))}`)}
                            >
                                <div className={styles.goodsItemImageBox}>
                                    <img
                                        className={styles.goodsItemImage}
                                        src={typeof item.goods_images?.[0] === 'string'
                                            ? item.goods_images[0]
                                            : item.goods_images?.[0]?.imgurl || item.imgurl || ''}
                                        alt={item.goods_name}
                                        loading="lazy"
                                    />
                                    {item.isEnded && (
                                        <div className={classNames(styles.goodsItemTag, styles.goodsItemTagGray)}>
                                            {item.endedTagText || '已结束'}
                                        </div>
                                    )}
                                    {/* 超过一小时显示固定日期和时间，不渲染倒计时。 */}
                                    {!item.isEnded && !item.saleStarted && item.remainingTime > ONE_HOUR && (
                                        <div className={styles.goodsItemTag}>
                                            {formatDateTime(item.listAt)}{getGoodsSaleActionText(item)}
                                        </div>
                                    )}
                                    {/* 一小时内只显示时分秒，使用 HH:mm:ss 格式。 */}
                                    {!item.isEnded && !item.saleStarted && item.remainingTime > 0 && item.remainingTime <= ONE_HOUR && (
                                        <div className={`${styles.goodsItemTag} ${styles.goodsItemCountdown}`}>
                                            <CountDown variant="h5"
                                                number={item.listAt}
                                                showDays={false}
                                                timeSeparator=":"
                                                endText={`后${getGoodsSaleActionText(item)}`}
                                                endTextStylesData={{marginLeft: px2rem(10)}}
                                                itemStylesData={{
                                                    padding: 0,
                                                    margin: 0,
                                                    height: 'auto',
                                                    fontSize: 'inherit',
                                                    lineHeight: 'inherit',
                                                    background: 'transparent',
                                                    color: 'var(--ant-primary-color)',
                                                }}
                                                boxStylesData={{ color: 'var(--ant-primary-color)', fontSize: 'inherit' }}
                                                onEnd={() => onGoodsSaleStart?.(String(item.code))}
                                            />
                                        </div>
                                    )}
                                    {/* 进行中商品的结束时间：超过一小时显示固定时间，一小时内显示实时倒计时。 */}
                                    {!item.isEnded && (item.saleStarted || item.remainingTime <= 0) && item.endAt > 0 && item.endRemainingTime > ONE_HOUR && (
                                        <div className={styles.goodsItemTag}>
                                            {formatDateTime(item.endAt)}{getGoodsEndActionText(item)}
                                        </div>
                                    )}
                                    {!item.isEnded && (item.saleStarted || item.remainingTime <= 0) && item.endAt > 0 && item.endRemainingTime > 0 && item.endRemainingTime <= ONE_HOUR && (
                                        <div className={`${styles.goodsItemTag} ${styles.goodsItemCountdown}`}>
                                            <CountDown variant="h5"
                                                number={item.endAt}
                                                showDays={false}
                                                timeSeparator=":"
                                                endText={`后${getGoodsEndActionText(item)}`}
                                                endTextStylesData={{marginLeft: px2rem(10)}}
                                                itemStylesData={{
                                                    padding: 0,
                                                    margin: 0,
                                                    height: 'auto',
                                                    fontSize: 'inherit',
                                                    lineHeight: 'inherit',
                                                    background: 'transparent',
                                                    color: 'var(--ant-primary-color)',
                                                }}
                                                boxStylesData={{ color: 'var(--ant-primary-color)', fontSize: 'inherit' }}
                                                onEnd={() => this.setState(state => ({ saleTimingVersion: state.saleTimingVersion + 1 }))}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className={styles.goodsItemName}>
                                    {/* 抽奖类型商品在名称前展示专属标识。 */}
                                    {isLotteryGoods(item) && (
                                        <img
                                            className={styles.goodsItemLotteryIcon}
                                            src={`${BASE_IMG_URL}/goods/icon11.png`}
                                            alt=""
                                        />
                                    )}
                                    {/* 兑换类型商品名称前展示当前用户等级和商城折扣。 */}
                                    {!isLotteryGoods(item) && this.renderLevelDiscountTag(styles.goodsItemDiscountTag, item)}
                                    {item.goods_name}
                                </div>
                                <div className={styles.goodsItemMeta}>
                                    {/* 已结束商品使用灰色V币图标及文字。 */}
                                    <div className={classNames(styles.goodsItemScore, item.isEnded && styles.goodsItemScoreSoldOut)}>
                                        <img
                                            src={`${BASE_IMG_URL}/goods/${item.isEnded ? 'icon10' : 'icon2'}.png`}
                                            alt=""
                                            className={styles.goodsItemCoin}
                                        />
                                        <div>{getGoodsPayScore(item)}</div>
                                    </div>
                                    {/* 仅可抽奖或可兑换的列表商品显示库存。 */}
                                    {item.showStock && <div className={styles.goodsItemStock}>库存 {item.goods_stock}</div>}
                                </div>
                            </div>
                        ))}
                        {!goodsListLoading && recommendedGoods.length === 0 && (
                            <div className={styles.goodsListStatus}>暂无商品</div>
                        )}
                    </div>
                </div>

                {/* TopNav 吸顶时隐藏底部状态栏，避免与商品列表浏览区域同时占位。 */}
                {!showFixedNav && <div className={classNames(
                    styles.bottomBar,
                    hasLotteryTimingInfo && styles.bottomBarWithTiming,
                    isLotteryDrawing && styles.bottomBarWithLotteryDrawing,
                )}>
                    {/* 我的V币入口，未登录时先触发登录。 */}
                    <div className={styles.sideAction} onClick={this.handleVCoinClick}>
                        <div className={styles.vCoinBox}>
                            <img src={`${BASE_IMG_URL}/goods/icon1.png`} alt="" className={styles.vCoin} />
                            <div className={styles.coinAmount}>
                                {isUserLoggedIn ? formatNumberToWan(user?.userInfo?.score ?? 0) : '请登录'}
                            </div>
                        </div>

                        <div className={styles.actiontext}>我的V币</div>

                    </div>
                    {/* 主操作区根据开抢、抽奖、兑换和结束状态切换内容。 */}
                    <div className={styles.exchangeAction}>
                        {hasLotteryTimingInfo && (
                            <div className={styles.lotteryEndInfo}>
                                {/* 开抢前超过一小时显示固定时间，一小时内显示实时倒计时。 */}
                                {showSaleStartDateTime && saleStartDateTimeParts && (
                                    <>
                                        <span className={styles.lotteryEndNumber}>{saleStartDateTimeParts.month}</span>月
                                        <span className={styles.lotteryEndNumber}>{saleStartDateTimeParts.day}</span>日
                                        <span className={styles.lotteryEndNumber}>{saleStartDateTimeParts.hour}</span>时
                                        <span className={styles.lotteryEndNumber}>{saleStartDateTimeParts.minute}</span>分
                                        <span className={styles.lotteryEndText}>{saleActionText}</span>
                                    </>
                                )}
                                {showSaleStartDateTime && !saleStartDateTimeParts && `${formatDateTime(saleStartAt)}${saleActionText}`}
                                {showSaleStartCountdown && (
                                    <CountDown variant="h5"
                                        number={saleStartAt}
                                        showDays={false}
                                        endText={`后${saleActionText}`}
                                        endTextStylesData={{marginLeft: px2rem(10)}}
                                        itemStylesData={{
                                            height: px2rem(44),
                                            lineHeight: px2rem(44),
                                            background: '#242933',
                                            fontSize: px2rem(28)
                                        }}
                                        onEnd={this.handleGoodsSaleStart}
                                    />
                                )}
                                {/* 一小时以上显示固定结束时间，月日时分的数字使用独立背景块。 */}
                                {showLotteryEndDateTime && lotteryEndDateTimeParts && (
                                    <>
                                        <span className={styles.lotteryEndNumber}>{lotteryEndDateTimeParts.month}</span>月
                                        <span className={styles.lotteryEndNumber}>{lotteryEndDateTimeParts.day}</span>日
                                        <span className={styles.lotteryEndNumber}>{lotteryEndDateTimeParts.hour}</span>时
                                        <span className={styles.lotteryEndNumber}>{lotteryEndDateTimeParts.minute}</span>分
                                        <span className={styles.lotteryEndText}>结束抽奖</span>
                                    </>
                                )}
                                {showLotteryEndDateTime && !lotteryEndDateTimeParts && `${formatDateTime(lotteryEndAt)}结束抽奖`}
                                {showLotteryEndCountdown && (
                                    <CountDown variant="h5"
                                        number={lotteryEndAt}
                                        showDays={false}
                                        endText="后结束抽奖"
                                        endTextStylesData={{marginLeft: px2rem(10)}}
                                        itemStylesData={{
                                            height: px2rem(44),
                                            lineHeight: px2rem(44),
                                            background: '#242933',
                                            fontSize: px2rem(28)
                                        }}
                                    />
                                )}
                            </div>
                        )}
                        {/* 开抢前使用真实链接保留用户点击手势，避免手机浏览器拦截日程 Intent。 */}
                        <a
                            href={saleReminderIntent || undefined}
                            className={buttonClassName}
                            onClick={isLotteryDrawing ? undefined : buttonClickHandler}
                        >
                            {isLotteryDrawing ? (
                                <span className={styles.lotteryDrawingContent}>
                                    <img
                                        src={`${BASE_IMG_URL}/goods/icon7.png`}
                                        alt=""
                                        className={styles.lotteryDrawingIcon}
                                        draggable={false}
                                    />
                                    <span className={styles.exchangeButtonText}>锦鲤附体</span>
                                </span>
                            ) : (
                                <>
                                    <span className={styles.exchangeButtonTitle}>
                                        <span className={styles.exchangeButtonText}>{buttonText}</span>
                                        {showExchangeDiscountButton && this.renderLevelDiscountTag(styles.exchangeButtonDiscountTag)}
                                    </span>
                                    {isLotteryInProgress && (
                                        <span className={styles.exchangeButtonSubText}>{lotteryPayScore}V币/次</span>
                                    )}
                                    {showPhysicalExchangeQuota && (
                                        <span className={styles.exchangeButtonQuotaText}>
                                            剩余兑换次数：{goodsData?.remaining_quota}
                                        </span>
                                    )}
                                </>
                            )}
                        </a>
                    </div>
                    {/* 奖品管理入口，未登录时先触发登录。 */}
                    <div
                        className={styles.sideAction}
                        onClick={this.handlePrizeManagementClick}
                    >
                        <div className={styles.vCoinBox}>
                            <img src={`${BASE_IMG_URL}/goods/icon4.png`} alt="" className={styles.giftIcon} />
                            {!isUserLoggedIn && <div className={styles.coinAmount}>请登录</div>}
                        </div>
                        <div className={styles.actiontext}>奖品管理</div>
                    </div>
                </div>}
                {/* 页面业务弹层：兑换、余额不足、结果和抽奖动画。 */}
                {this.renderExchangePopup()}
                {this.renderInsufficientPopup()}
                {this.renderExchangeSuccessPopup()}
                {this.renderLotteryAnimation()}
                {this.renderLotteryEncouragementPopup()}
                {this.renderLotteryWinPopup()}
                {this.state.weChatBrowserGuideVisible && (
                    <div
                        className={styles.weChatBrowserGuide}
                        onClick={() => this.setState({ weChatBrowserGuideVisible: false })}
                    >
                        <div className={styles.weChatBrowserGuideTip}>
                            点击右上角选择“在浏览器中访问”
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default withRouter(GoodsDetailH5);
